<script setup lang='ts'>
import { CalendarDays, Search } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';
import { goToRoute, useFuseLandingSearch } from '@/utils/utils';

import TheCalendar from '../mode-list/TheCalendar.vue';

const emit = defineEmits<{ submit: [] }>();

const { t } = useI18n();
const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, currentLocale, routingOptions } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();

const metricsStore = useMetricsStore();
const { themes } = storeToRefs(metricsStore);

const activeTheme = computed(() => filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes')?.selection?.[0]) ?? undefined;
const activeThemeObject = computed(() => themes.value.find(it => it.id === activeTheme.value));
const hasCalendar = computed(() => globalConfiguration.value?.options?.hasCalendar ?? true);

const currentSearch = computed(() => filters.value.sorts.find(it => it.type === 'search')?.query ?? '');
const input = ref(currentSearch.value);
const isInputFocused = ref(false);

const {
  matchingResults: matchingRoutes,
  debouncedSearch: debouncedHandleLandingSuggestions,
} = useFuseLandingSearch(() => routingOptions.value ?? []);

function handleSearchInput(event: any) {
  filtersStore.addSearchingFilter(event.target.value);
}

function handleSearchSubmit(event: KeyboardEvent) {
  if (event.target instanceof HTMLInputElement)
    event.target.blur();
  filtersStore.addSearchingFilter(input.value);
  emit('submit');
}

function getTranslatedName(theme: any) {
  return theme.translatedName.find((it: any) => it.lang === currentLocale.value)?.value ?? theme.name;
}

function setTheme(theme: typeof themes.value[number]) {
  const activeTheme = filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes');
  const crossedTaxonomies = filters.value.metrics.find(it => it.type === 'crossedTaxonomyCount');
  filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'taxonomyTypes');

  if (activeTheme) {
    activeTheme.selection = [theme.id];
  }
  else {
    filters.value.selections.push({
      filterProperties: {
        type: 'taxonomyThemes',
      },
      selection: [theme.id],
    });
  }

  if (crossedTaxonomies) {
    crossedTaxonomies.crossedTaxonomies.themes = [theme.id];
  }
  else {
    filters.value.metrics.push({
      type: 'crossedTaxonomyCount',
      taxonomyTypes: ['TYPES'],
      crossedTaxonomies: {
        themes: [theme.id],
      },
    });
  }
  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}
</script>

<template>
  <div class="search-filter">
    <div class="filter-wrapper" tabindex="0">
      <Search :size="18" color="var(--yb-color-black)" aria-hidden="true" />
      <input
        v-model="input"
        type="search"
        :placeholder="t('search.button')"
        :aria-label="t('search.button')"
        @input="debouncedHandleLandingSuggestions(input, isInputFocused)"
        @change="(v) => handleSearchInput(v)"
        @keydown.enter.prevent="handleSearchSubmit"
        @focus="isInputFocused = true;"
        @focusout="isInputFocused = false;"
      >
    </div>
  </div>
  <div
    v-if="matchingRoutes.length > 0 && input.length >= 3 && isInputFocused"
    class="search-suggestions"
    @click.stop
  >
    <ul>
      <li
        v-for="route in matchingRoutes"
        :key="route.url"
        @mousedown.prevent="goToRoute(route.url)"
      >
        <div class="suggestion-item">
          <img
            v-if="route.thumbnail"
            :src="route.thumbnail"
            alt="aperçu de {{ route.title }}"
            class="suggestion-image"
            @error="route.thumbnail = 'https://empowher.org/wp-content/uploads/2021/03/image-placeholder-350x350-1-300x300.png'"
          >
          <span class="suggestion-title">
            {{ route.title }}
          </span>
        </div>
      </li>
    </ul>
  </div>
  <div class="themes">
    <button
      v-for="(item, index) in themes" :key="index" class="theme"
      :class="{ selected: activeThemeObject?.id === item.id }" tabindex="0" @click="setTheme(item)"
      @keydown.enter="setTheme(item)"
    >
      {{ getTranslatedName(item) }}
      <span v-if="item.count" class="count">{{ item.count }}</span>
    </button>
  </div>
  <div v-if="hasCalendar">
    <h3 class="dates-title">
      <span><CalendarDays :size="22" color="var(--yb-color-black)" aria-hidden="true" /></span>{{ t('search.dates') }}
    </h3>
    <TheCalendar :columns="1" :rows="2" />
  </div>
</template>

<style lang="css" scoped>
.dates-title {
  display: flex;
  align-items: center;
  margin-bottom: var(--yb-spacing-sm);
  margin-top: var(--yb-spacing-base);

  span {
    margin-right: var(--yb-spacing-sm);
  }
}

.search-filter {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .filter-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    gap: var(--yb-spacing-xs);
    padding: var(--yb-spacing-sm);
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: var(--yb-radius-sm);
    margin-bottom: var(--yb-spacing-sm);

    input {
      background-color: transparent;
      outline: none;
      border: none;
      color: var(--yb-color-black);
      font-size: var(--yb-text-base);
      font-weight: var(--yb-text-semi-bold);
      caret-color: var(--yb-color-primary);

      &::placeholder {
        color: var(--yb-color-black);
      }
    }
  }
}

.search-suggestions {
  width: 100%;
  max-height: calc((var(--yb-spacing-sm) * 2 + var(--yb-text-base) * 1.5) * 3.5);
  background-color: white;
  margin-top: var(--yb-spacing-xs);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  z-index: 10;
  overflow-y: auto;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: var(--yb-spacing-sm) var(--yb-spacing-sm);
    cursor: pointer;
    font-size: var(--yb-text-base);
    transition: background-color 0.2s ease;
  }

  li:hover {
    background-color: var(--yb-color-lightgray);
  }

  .suggestion-item {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-sm);
  }

  .suggestion-image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: var(--yb-radius-sm);
  }
}

.themes {
  margin-top: var(--yb-spacing-sm);

  .theme {
    display: flex;
    align-items: center;
    width: 100%;
    white-space: nowrap;
    color: var(--yb-color-black);
    padding: var(--yb-spacing-xs) var(--yb-spacing-sm);
    background-color: var(--yb-color-white);
    border: 1px solid var(--yb-color-lightgray);
    outline-color: var(--yb-color-primary);
    border-radius: var(--yb-radius-sm);
    margin-bottom: var(--yb-spacing-xs);

    &.selected {
      border: 1px solid var(--yb-color-darkgray);
      background-color: var(--search-highlight-background-color);
    }
  }
}

.count {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--yb-text-xs);
  font-weight: var(--yb-text-semi-bold);
  background-color: var(--yb-color-black);
  color: var(--yb-color-white);
  aspect-ratio: 1;
  padding: 2px 4px;
  min-width: 1rem;
  margin-left: auto;
  border-radius: 4px;
}

/* clears the cross to clear the input from Internet Explorer */
input[type='search']::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}

input[type='search']::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}

/* clears the cross to clear the input from Chrome */
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}
</style>
